import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import logger from "../utils/logger";
import { showToast } from "./Toast";

const FeedbackModal = ({ isOpen, onClose, variant = "full", onOpenFullModal, storyId = null, contentType = "video", sessionId = null }) => {
  const { user } = useAuth();
  const { apiClient } = useAPI();
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartTimeRef = useRef(null);
  const recognitionRef = useRef(null);
  const [transcript, setTranscript] = useState("");
  
  // Analytics tracking guard
  const hasTrackedModalShown = useRef(false);

  // Shared feedback context (ensures consistency across all events)
  const feedbackContext = {
    content_type: contentType,
    story_id: storyId || null,
    session_id: sessionId,
  };

  // Feedback analytics helper (fire-and-forget)
  const logFeedbackEvent = async ({ eventType, optionType = null, ...context }) => {
    try {
      const payload = {
        user_id: user?.id || null,
        event_type: eventType,
        option_type: optionType,
        ...context, // Spread passed context (content_type, story_id, session_id)
      };

      // Dev logging
      if (process.env.NODE_ENV === "development") {
        console.log("[Feedback Analytics]", payload);
      }

      await fetch(`${apiClient.baseURL}/feedback-events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiClient.token && { Authorization: `Bearer ${apiClient.token}` }),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      // Silent failure - don't block UI
      console.log("ℹ️ Feedback analytics error (non-blocking):", error);
    }
  };

  // Track MODAL_SHOWN event (full modal only, once per open)
  useEffect(() => {
    if (isOpen && variant === "full" && !hasTrackedModalShown.current) {
      hasTrackedModalShown.current = true;
      logFeedbackEvent({ 
        eventType: "MODAL_SHOWN",
        ...feedbackContext 
      });
    }
    
    // Reset guard when modal closes
    if (!isOpen) {
      hasTrackedModalShown.current = false;
    }
  }, [isOpen, variant]);

  // Cleanup on unmount - stop recording if active
  useEffect(() => {
    return () => {
      if (isRecording && mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [isRecording]);

  // Opens the feedback textarea (does NOT submit or close)
  const handleQuickFeedback = () => {
    logFeedbackEvent({ 
      eventType: "OPTION_CLICKED", 
      optionType: "leave_feedback",
      ...feedbackContext 
    });
    setIsSubmitting(true);
  };

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create AudioContext to properly route audio
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const destination = audioContext.createMediaStreamDestination();
      
      // Connect mic input to destination (creates proper audio routing)
      source.connect(destination);
      
      logger.log("🎧 AudioContext created and audio routing established");
      
      // Detect supported audio mimeType for reliable recording
      let mimeType = 'audio/webm;codecs=opus'; // Preferred format
      
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Fallback options if webm with opus not supported
        const fallbacks = [
          'audio/webm',
          'audio/mp4',
          'audio/ogg;codecs=opus',
          'audio/wav'
        ];
        
        mimeType = fallbacks.find(type => MediaRecorder.isTypeSupported(type)) || '';
        
        if (!mimeType) {
          logger.warn("⚠️ No supported audio mimeType found, using browser default");
        } else {
          logger.log(`✅ Using audio mimeType: ${mimeType}`);
        }
      } else {
        logger.log(`✅ Using preferred audio mimeType: ${mimeType}`);
      }
      
      // Create MediaRecorder with AudioContext destination stream
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(destination.stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        // Use the same mimeType for the Blob that was used for recording
        const actualMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        const duration = (Date.now() - recordingStartTimeRef.current) / 1000;
        setAudioBlob(audioBlob);
        setRecordingDuration(duration);
        
        logger.log(`🎤 Audio recorded: ${(audioBlob.size / 1024).toFixed(2)} KB, type: ${actualMimeType}`);
        
        // Clean up AudioContext and tracks
        source.disconnect();
        audioContext.close().catch(err => logger.log("AudioContext close error:", err));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      recordingStartTimeRef.current = Date.now();
      logger.log("🎤 Voice recording started");
      
      // Start speech recognition AFTER a delay (optional enhancement)
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        // Delay to let MediaRecorder stabilize and reduce "no-speech" errors
        setTimeout(() => {
          try {
            const recognition = new SpeechRecognition();
            recognition.continuous = false; // Single recognition session
            recognition.interimResults = false; // Only final results
            recognition.lang = 'en-US';
            recognition.maxAlternatives = 1;
            
            recognition.onresult = (event) => {
              try {
                let finalTranscript = '';
                
                for (let i = 0; i < event.results.length; i++) {
                  if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript + ' ';
                  }
                }
                
                if (finalTranscript.trim().length > 0) {
                  // Append transcribed text to feedbackText (textarea)
                  setFeedbackText(prev => {
                    const newText = prev + (prev ? ' ' : '') + finalTranscript;
                    logger.log("🗣️ Speech recognized and added to textarea:", finalTranscript.trim());
                    return newText;
                  });
                  
                  // Also store in transcript state for reference
                  setTranscript(prev => {
                    const newTranscript = prev + finalTranscript;
                    return newTranscript;
                  });
                }
              } catch (err) {
                // Silently fail - speech-to-text is optional
                logger.log("ℹ️ Speech recognition result processing failed (optional feature)");
              }
            };
            
            recognition.onerror = (event) => {
              // All speech recognition errors are treated as NORMAL
              if (event.error === 'no-speech') {
                logger.log("ℹ️ No speech detected (this is normal)");
              } else if (event.error === 'aborted') {
                logger.log("ℹ️ Speech recognition aborted");
              } else {
                logger.log("ℹ️ Speech recognition status:", event.error);
              }
              // Never throw or propagate errors
            };
            
            recognition.onend = () => {
              // Best-effort only - do not retry
              logger.log("ℹ️ Speech recognition ended");
            };
            
            recognitionRef.current = recognition;
            recognition.start();
            logger.log("🗣️ Speech-to-text enabled (optional enhancement)");
          } catch (error) {
            logger.log("ℹ️ Speech recognition unavailable - voice recording will continue");
          }
        }, 400); // 400ms delay
      }
    } catch (error) {
      logger.error("Error starting recording:", error);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Stop speech recognition (if running) - never throw
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Already stopped or failed, ignore silently
        }
      }
      
      // NEW UX: DO NOT append transcript or fallback to textarea
      // Audio is handled separately from text input
      // audioBlob and recordingDuration are already set in mediaRecorder.onstop
      
      logger.log("✅ Voice recording stopped successfully");
      
      // Optional: Log transcript if available (for debugging)
      if (transcript && transcript.trim().length > 0) {
        logger.log("ℹ️ Speech transcript (not added to textarea):", transcript.trim());
      }
    }
  };

  // Cancel recording (discard audio)
  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        // Already stopped, ignore
      }
      
      // Stop all tracks immediately
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
    
    // Stop speech recognition - never throw
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Already stopped or failed, ignore
      }
    }
    
    setIsRecording(false);
    setAudioBlob(null);
    setRecordingDuration(0);
    setTranscript("");
    audioChunksRef.current = [];
    logger.log("🎤 Voice recording cancelled");
  };

  // Submits the feedback and closes the modal
  const handleSubmitFeedback = async () => {
    // Determine final text to submit
    // Priority: typed text > transcript from voice
    let finalText = feedbackText.trim();
    
    // If no typed text but we have a transcript from voice recording
    if (!finalText && transcript.trim()) {
      finalText = transcript.trim();
    }
    
    // Validate we have some feedback to submit
    if (!finalText) {
      return;
    }
    
    // Validate storyId is present
    if (!storyId) {
      logger.warn("⚠️ FeedbackModal: storyId is missing. Cannot submit feedback without storyId.");
      alert("Unable to submit feedback: Story ID is missing.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Build FormData
      const formData = new FormData();
      formData.append('content_type', contentType); // "audio" or "video"
      formData.append('feedback_type', "text"); // Always "text" (typed or transcribed)
      formData.append('text', finalText);
      
      logger.log("📤 Submitting feedback:", {
        storyId,
        contentType,
        feedbackType: "text",
        textLength: finalText.length
      });
      
      // Send POST request to backend
      // Note: Using raw fetch instead of apiClient.request to properly handle FormData
      const url = `${apiClient.baseURL}/stories/${storyId}/feedback`;
      const headers = {};
      
      // Add authorization token if available
      if (apiClient.token) {
        headers.Authorization = `Bearer ${apiClient.token}`;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: headers, // Only Authorization, no Content-Type - let browser set multipart/form-data
        body: formData,
      });
      
      // Parse response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      logger.log("✅ Feedback submitted successfully:", result);
      
      // Track successful submission
      logFeedbackEvent({ 
        eventType: "FEEDBACK_SUBMITTED",
        ...feedbackContext 
      });
      
      // Show success toast
      showToast("Thanks for your feedback 💜", "success");
      
      // Reset state and close modal on success
      setFeedbackText("");
      setAudioBlob(null);
      setRecordingDuration(0);
      setIsRecording(false);
      setTranscript("");
      onClose();
      
    } catch (error) {
      logger.error("❌ Error submitting feedback:", error);
      alert(`Failed to submit feedback: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookCall = () => {
    logFeedbackEvent({ 
      eventType: "OPTION_CLICKED", 
      optionType: "book_call",
      ...feedbackContext 
    });
    window.open(
      "https://calendly.com/admin-pixleyai/new-meeting?month=2025-12",
      "_blank",
      "noopener,noreferrer"
    );
    onClose();
  };

  const handleNotNow = () => {
    logFeedbackEvent({ 
      eventType: "MODAL_DISMISSED", 
      optionType: "not_now",
      ...feedbackContext 
    });
    onClose();
  };

  if (!isOpen) return null;

  // Full-screen modal variant (first-time users)
  if (variant === "full") {
    const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)", fontFamily: "'Inter', 'Fredoka', sans-serif" }}
      >
        <div
          className="relative max-w-2xl w-full mx-4 p-8 sm:p-10 overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 28,
            boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
          }}
        >
          {/* Soft blue accent blob */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -90,
              right: -90,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: "rgba(0,101,248,0.10)",
              filter: "blur(20px)",
              pointerEvents: "none",
            }}
          />

          <div className="relative">
            {/* Header */}
            <div className="mb-7 text-center">
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.20em", textTransform: "uppercase" }}>
                Feedback
              </span>
              <h2
                style={{
                  ...FREDOKA,
                  color: "#0B1A33",
                  fontWeight: 700,
                  fontSize: "clamp(1.6rem, 3.4vw, 2.2rem)",
                  textTransform: "uppercase",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                  margin: "6px 0 8px",
                }}
              >
                How can we make <span style={{ color: "#0065F8" }}>Pixley</span> better?
              </h2>
              <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 15, margin: 0 }}>
                Your feedback helps us create better stories for kids.
              </p>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-5">
              {!isSubmitting && (
                <button
                  onClick={handleQuickFeedback}
                  className="w-full flex items-center gap-4 transition-all active:scale-[0.99]"
                  style={{
                    background: "#FFFFFF",
                    border: "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 18,
                    padding: "16px 18px",
                    boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(0,101,248,0.35)"; e.currentTarget.style.boxShadow = "0px 3px 0px rgba(0,101,248,0.18)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.08)"; e.currentTarget.style.boxShadow = "0px 3px 0px rgba(11,26,51,0.06)"; }}
                >
                  <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                      color: "#FFFFFF",
                      boxShadow: "0px 2px 0px #073B8E",
                    }}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 16, margin: 0 }}>Leave quick feedback</p>
                    <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "2px 0 0" }}>Share your thoughts (text or voice)</p>
                  </div>
                  <svg width="16" height="16" fill="none" stroke="#94A3B8" strokeWidth="2.4" viewBox="0 0 24 24" className="flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              <button
                onClick={handleBookCall}
                className="w-full flex items-center gap-4 transition-all active:scale-[0.99]"
                style={{
                  background: "#FFFFFF",
                  border: "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 18,
                  padding: "16px 18px",
                  boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(0,101,248,0.35)"; e.currentTarget.style.boxShadow = "0px 3px 0px rgba(0,101,248,0.18)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.08)"; e.currentTarget.style.boxShadow = "0px 3px 0px rgba(11,26,51,0.06)"; }}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, #00CAFF 0%, #0065F8 100%)",
                    color: "#FFFFFF",
                    boxShadow: "0px 2px 0px #073B8E",
                  }}
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 16, margin: 0 }}>Book a 15-min call</p>
                  <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "2px 0 0" }}>Get a thank-you gift</p>
                </div>
                <svg width="16" height="16" fill="none" stroke="#94A3B8" strokeWidth="2.4" viewBox="0 0 24 24" className="flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Quick Feedback Form */}
            {isSubmitting && (
              <div
                className="mb-5 overflow-hidden"
                style={{
                  background: "#FFFFFF",
                  border: "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 20,
                  padding: 18,
                  boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
                }}
              >
                <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
                  Your Feedback
                </label>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell us what you think…"
                  className="w-full"
                  style={{
                    ...FREDOKA,
                    height: 128,
                    background: "#F5FAFF",
                    color: "#0B1A33",
                    fontSize: 14,
                    fontWeight: 500,
                    border: "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 14,
                    padding: "12px 14px",
                    outline: "none",
                    resize: "none",
                    transition: "border-color 200ms, box-shadow 200ms",
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
                  autoFocus
                />

                {/* Voice Recording */}
                <div className="mt-4 space-y-2.5">
                  {!isRecording && !audioBlob && (
                    <div className="space-y-1.5">
                      <button
                        onClick={startRecording}
                        className="inline-flex items-center gap-2 transition active:scale-[0.97]"
                        style={{
                          ...FREDOKA,
                          background: "#FFFFFF",
                          color: "#0065F8",
                          fontWeight: 700,
                          fontSize: 13,
                          border: "2px solid rgba(0,101,248,0.22)",
                          borderRadius: 999,
                          padding: "9px 16px",
                          boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                          cursor: "pointer",
                        }}
                      >
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
                        </svg>
                        Record voice feedback
                      </button>
                      <p style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11, fontStyle: "italic", margin: 0 }}>
                        Optional · adds to your text feedback
                      </p>
                    </div>
                  )}

                  {isRecording && (
                    <div className="space-y-2">
                      <div
                        className="flex items-center gap-3 p-3"
                        style={{
                          background: "linear-gradient(180deg, #FFE9EE 0%, #FFD6E2 100%)",
                          border: "2px solid rgba(255,107,154,0.30)",
                          borderRadius: 14,
                          boxShadow: "0px 2px 0px rgba(255,107,154,0.18)",
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: "#FF6B9A",
                              boxShadow: "0 0 0 4px rgba(255,107,154,0.30)",
                              animation: "fb-pulse 1s ease-in-out infinite",
                            }}
                          />
                          <span style={{ ...FREDOKA, color: "#BE185D", fontWeight: 700, fontSize: 13 }}>Recording…</span>
                        </div>
                        <button
                          onClick={cancelRecording}
                          aria-label="Cancel recording"
                          className="transition active:scale-[0.94]"
                          style={{
                            width: 30, height: 30,
                            background: "#FFFFFF",
                            color: "#BE185D",
                            border: "2px solid rgba(255,107,154,0.30)",
                            borderRadius: 10,
                            boxShadow: "0px 1px 0px rgba(255,107,154,0.15)",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <button
                          onClick={stopRecording}
                          className="transition active:scale-[0.97]"
                          style={{
                            ...FREDOKA,
                            background: "#FF6B9A",
                            color: "#FFFFFF",
                            fontWeight: 700,
                            fontSize: 12,
                            border: "none",
                            borderRadius: 999,
                            padding: "7px 14px",
                            boxShadow: "0px 2px 0px #C94D73",
                            cursor: "pointer",
                          }}
                        >
                          Stop
                        </button>
                      </div>
                      <p style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11, fontStyle: "italic", margin: 0 }}>
                        Speak clearly — click Stop when finished
                      </p>
                    </div>
                  )}

                  {!isRecording && audioBlob && (
                    <div
                      className="space-y-2 p-3"
                      style={{
                        background: "#F5FAFF",
                        border: "2px solid rgba(0,101,248,0.22)",
                        borderRadius: 14,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <svg width="16" height="16" fill="#0065F8" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span style={{ ...FREDOKA, color: "#0065F8", fontWeight: 700, fontSize: 13 }}>
                          Voice recorded · {recordingDuration.toFixed(1)}s
                        </span>
                      </div>
                      <audio controls src={URL.createObjectURL(audioBlob)} className="w-full h-10" style={{ borderRadius: 8 }} />
                      <button
                        onClick={() => { setAudioBlob(null); setRecordingDuration(0); setTranscript(""); }}
                        className="w-full inline-flex items-center justify-center gap-2 transition active:scale-[0.97]"
                        style={{
                          ...FREDOKA,
                          background: "#FFFFFF",
                          color: "#DC2626",
                          fontWeight: 700,
                          fontSize: 12,
                          border: "2px solid rgba(220,38,38,0.25)",
                          borderRadius: 999,
                          padding: "8px 14px",
                          boxShadow: "0px 2px 0px rgba(220,38,38,0.10)",
                          cursor: "pointer",
                        }}
                      >
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove audio
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      logFeedbackEvent({ eventType: "MODAL_DISMISSED", optionType: "cancel", ...feedbackContext });
                      setIsSubmitting(false);
                      setFeedbackText("");
                      setAudioBlob(null);
                      setRecordingDuration(0);
                      setIsRecording(false);
                      setTranscript("");
                      if (mediaRecorderRef.current) cancelRecording();
                    }}
                    className="transition active:scale-[0.97]"
                    style={{
                      ...FREDOKA,
                      background: "#FFFFFF",
                      color: "#5A6B85",
                      fontWeight: 700,
                      fontSize: 13,
                      border: "2px solid rgba(11,26,51,0.10)",
                      borderRadius: 999,
                      padding: "9px 18px",
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitFeedback}
                    disabled={!feedbackText.trim() && !transcript.trim()}
                    className="transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      ...FREDOKA,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      border: "none",
                      borderRadius: 999,
                      padding: "10px 22px",
                      boxShadow: "0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.20)",
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )}

            {/* Not Right Now */}
            <div className="text-center">
              <button
                onClick={handleNotNow}
                style={{
                  ...FREDOKA,
                  background: "transparent",
                  color: "#94A3B8",
                  fontWeight: 600,
                  fontSize: 13,
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 8px",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#5A6B85")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#94A3B8")}
              >
                Not right now
              </button>
            </div>
          </div>

          <style>{`@keyframes fb-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.4); opacity: 0.6; } }`}</style>
        </div>
      </div>
    );
  }

  // Small notification variant (returning users)
  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
  return (
    <div className="fixed top-20 right-4 z-[100] max-w-[280px] w-auto" style={{ fontFamily: "'Inter', 'Fredoka', sans-serif" }}>
      <div
        className="relative overflow-hidden"
        style={{
          background: "#FFFFFF",
          border: "2px solid rgba(11,26,51,0.06)",
          borderRadius: 18,
          padding: "14px 14px 12px",
          boxShadow: "0px 5px 0px rgba(11,26,51,0.08), 0px 18px 32px rgba(11,26,51,0.14)",
        }}
      >
        {/* Soft blue accent blob */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(0,101,248,0.10)",
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />
        <div className="relative">
          <div className="flex items-start gap-2.5 mb-2.5">
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                color: "#FFFFFF",
                boxShadow: "0px 2px 0px #073B8E",
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 14, margin: 0, lineHeight: 1.2 }}>
                Have thoughts?
              </p>
              <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, margin: "2px 0 0", lineHeight: 1.3 }}>
                Help us improve Pixley.
              </p>
            </div>
            <button
              onClick={() => {
                logFeedbackEvent({
                  eventType: "MODAL_DISMISSED",
                  optionType: "notification_closed",
                  ...feedbackContext,
                });
                onClose();
              }}
              aria-label="Close notification"
              className="transition active:scale-[0.94] flex-shrink-0"
              style={{
                width: 24,
                height: 24,
                background: "#FFFFFF",
                color: "#94A3B8",
                border: "2px solid rgba(11,26,51,0.08)",
                borderRadius: 8,
                boxShadow: "0px 1px 0px rgba(11,26,51,0.05)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            onClick={() => {
              onClose();
              if (onOpenFullModal) {
                onOpenFullModal();
              } else {
                window.dispatchEvent(new CustomEvent("openFeedbackModal"));
              }
            }}
            className="w-full inline-flex items-center justify-center gap-1.5 transition active:scale-[0.97]"
            style={{
              ...FREDOKA,
              background: "#0065F8",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 13,
              border: "none",
              borderRadius: 999,
              padding: "9px 14px",
              boxShadow: "0px 3px 0px #073B8E",
              cursor: "pointer",
            }}
          >
            Share Feedback
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
